// src/utils/dateMath.js

/**
 * HTML `<input type="date">` values are `YYYY-MM-DD`. `new Date("YYYY-MM-DD")` uses **UTC** midnight,
 * so `getDate()` in US timezones is often the **previous** local calendar day — wrong Monday-of-week and week counts.
 * Parse as the user’s local civil date instead.
 * @param {string|Date} dateInput
 * @returns {Date|null} local midnight for that calendar day, or null
 */
export function parseLocalCalendarDate(dateInput) {
  if (dateInput == null || dateInput === "") return null;
  if (typeof dateInput === "string") {
    const m = dateInput.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      const dt = new Date(y, mo, d);
      if (
        dt.getFullYear() === y &&
        dt.getMonth() === mo &&
        dt.getDate() === d
      ) {
        return dt;
      }
      return null;
    }
  }
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Given a calendar day (user-chosen "start training" date), returns `YYYY-MM-DD`
 * for the **Monday** of the Monday–Sunday week that contains that day (local calendar).
 * Matches `computeWeeks` / form inputs that use plain dates, not UTC offsets.
 * @param {string|Date} dateInput - e.g. race picker value or Date
 * @returns {string|null} ISO-like `YYYY-MM-DD` or null if invalid
 */
export function mondayOfWeekContaining(dateInput) {
  const local = parseLocalCalendarDate(dateInput);
  if (!local) return null;

  const dow = local.getDay(); // 0 Sun … 6 Sat
  const daysFromMonday = (dow + 6) % 7; // Mon = 0, Sun = 6
  local.setDate(local.getDate() - daysFromMonday);

  const y = local.getFullYear();
  const mo = String(local.getMonth() + 1).padStart(2, "0");
  const day = String(local.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/**
 * Calendar weeks from the **Monday of the start week** through the **Monday of the week that contains the race** (inclusive).
 * Matches PaceWorks “align to Monday of start week”: moving the race within the same Mon–Sun week does not change the count
 * (avoids `ceil((race - start + 1d) / 7d)` jumping an extra week Sat → Sun).
 */
export function computeWeeks(startDateInput, raceDateInput) {
  const startMid = parseLocalCalendarDate(startDateInput);
  const raceMid = parseLocalCalendarDate(raceDateInput);
  if (!startMid || !raceMid) {
    return 1;
  }

  const MS_DAY = 24 * 60 * 60 * 1000;

  const startMonday = new Date(
    startMid.getFullYear(),
    startMid.getMonth(),
    startMid.getDate()
  );
  {
    const dow = startMonday.getDay();
    const daysFromMonday = (dow + 6) % 7;
    startMonday.setDate(startMonday.getDate() - daysFromMonday);
  }

  const raceWeekMonday = new Date(
    raceMid.getFullYear(),
    raceMid.getMonth(),
    raceMid.getDate()
  );
  {
    const dow = raceWeekMonday.getDay();
    const daysFromMonday = (dow + 6) % 7;
    raceWeekMonday.setDate(raceWeekMonday.getDate() - daysFromMonday);
  }

  const diffDays = (raceWeekMonday - startMonday) / MS_DAY;
  if (diffDays < 0) return 1;

  return Math.max(1, Math.floor(diffDays / 7) + 1);
}
